import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LayoutProvider } from './contexts/LayoutContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LanguageWrapper, LanguageRedirect, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './components/routing/LanguageWrapper';
import { AppLayout } from './components/layout/AppLayout';
import { DashboardPage } from './pages/DashboardPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { MapPageView } from './pages/MapPageView';
import { StationsPage } from './pages/StationsPage';
import { ReportsPage } from './pages/ReportsPage';
import { ReportsHubPage } from './pages/ReportsHubPage';
import { ReportWorkspacePage } from './pages/ReportWorkspacePage';
import { ReportReviewPage } from './pages/ReportReviewPage';
import { ReportWizardPage } from './pages/ReportWizardPage';
import { ReportPrintPage } from './pages/ReportPrintPage';
import { SettingsPage } from './pages/SettingsPage';
import { UsersPage } from './pages/UsersPage';
import { HelpPage } from './pages/HelpPage';
import { ComponentsShowcasePage } from './pages/ComponentsShowcasePage';
import { LoginPage } from './pages/LoginPage';
import { BRAND } from './config/brand';
import './i18n/config';
import './App.css';

import { ROLES, navigationPermissions } from './utils/rbac';

function BrandMetadata() {
  React.useEffect(() => {
    document.title = BRAND.browserTitle;

    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute('content', BRAND.browserDescription);
    }
  }, []);

  return null;
}

function App() {
  return (
    <Router>
      <BrandMetadata />
      <AuthProvider>
        <LayoutProvider>
          <AnalyticsProvider>
            <Routes>
              {/* Root redirect - sends to /:lang/dashboard based on preference */}
              <Route path="/" element={<LanguageRedirect />} />
              
              {/* Public login route - language-prefixed */}
              <Route path="/:lang/login" element={<LoginPage />} />
              
              {/* Legacy login route - redirect to language-prefixed version */}
              <Route path="/login" element={<Navigate to={`/${DEFAULT_LANGUAGE}/login`} replace />} />

              {/* Language-wrapped routes */}
              <Route path="/:lang" element={<LanguageWrapper />}>
                {/* Root of language - redirect to dashboard */}
                <Route index element={<Navigate to="dashboard" replace />} />
                
                {/* Protected Routes */}
                <Route path="dashboard" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.dashboard}>
                    <AppLayout><DashboardPage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="analytics" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.analytics}>
                    <AppLayout><AnalyticsPage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="map" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.map}>
                    <AppLayout><MapPageView /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="stations" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.stations}>
                    <AppLayout><StationsPage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="reports" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.reports}>
                    <AppLayout><ReportsHubPage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="reports/old" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.reports}>
                    <AppLayout><ReportsPage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="reports/:id" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.reports}>
                    <AppLayout><ReportWorkspacePage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="reports/:id/preview" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.reports}>
                    <AppLayout><ReportWorkspacePage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="reports/:id/review" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.reports}>
                    <AppLayout><ReportWorkspacePage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="reports/:id/print" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.reports}>
                    <ReportPrintPage />
                  </ProtectedRoute>
                } />
                <Route path="reports/:id/wizard" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.reports}>
                    <ReportWizardPage />
                  </ProtectedRoute>
                } />
                <Route path="settings" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.settings}>
                    <AppLayout><SettingsPage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="users" element={
                  <ProtectedRoute allowedRoles={navigationPermissions.users}>
                    <AppLayout><UsersPage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="help" element={
                  <ProtectedRoute>
                    <AppLayout><HelpPage /></AppLayout>
                  </ProtectedRoute>
                } />
              </Route>

              {/* Component Showcase (from Prompt 1) - no language prefix needed */}
              <Route path="/components" element={<ComponentsShowcasePage />} />
              
              {/* Legacy routes - redirect to language-prefixed versions */}
              <Route path="/dashboard" element={<Navigate to={`/${DEFAULT_LANGUAGE}/dashboard`} replace />} />
              <Route path="/analytics" element={<Navigate to={`/${DEFAULT_LANGUAGE}/analytics`} replace />} />
              <Route path="/map" element={<Navigate to={`/${DEFAULT_LANGUAGE}/map`} replace />} />
              <Route path="/stations" element={<Navigate to={`/${DEFAULT_LANGUAGE}/stations`} replace />} />
              <Route path="/reports" element={<Navigate to={`/${DEFAULT_LANGUAGE}/reports`} replace />} />
              <Route path="/reports/*" element={<Navigate to={`/${DEFAULT_LANGUAGE}/reports`} replace />} />
              <Route path="/settings" element={<Navigate to={`/${DEFAULT_LANGUAGE}/settings`} replace />} />
              <Route path="/users" element={<Navigate to={`/${DEFAULT_LANGUAGE}/users`} replace />} />
              <Route path="/help" element={<Navigate to={`/${DEFAULT_LANGUAGE}/help`} replace />} />
              
              {/* Catch-all - redirect to language-prefixed dashboard */}
              <Route path="*" element={<Navigate to={`/${DEFAULT_LANGUAGE}/dashboard`} replace />} />
            </Routes>
          </AnalyticsProvider>
        </LayoutProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
